import { useQuery, useMutation, UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '../client';

// Types for analytics data
export interface OverviewMetrics {
  totalSessions: number;
  totalRevenue: number;
  averageRating: number;
  reviewCount: number;
  uniqueClients: number;
  cancelledSessions: number;
  previousSessions: number;
  previousRevenue: number;
  previousClients: number;
  sessionsChange: number;
  revenueChange: number;
}

export interface RevenueDataPoint {
  date: string;
  revenue: number;
  sessionCount: number;
}

export interface RevenueChartData {
  period: string;
  startDate: string;
  endDate: string;
  data: RevenueDataPoint[];
}

export interface SessionBreakdown {
  byStatus: {
    COMPLETED: number;
    CONFIRMED: number;
    CANCELLED: number;
    PENDING_PAYMENT: number;
  };
  byType: {
    INTAKE: number;
    REGULAR: number;
    CRISIS: number;
  };
  total: number;
  completionRate: number;
  cancellationRate: number;
}

export interface TopClient {
  consumerId: number;
  consumerName?: string;
  sessionsInPeriod: number;
  totalSessions: number;
  sessionCount?: number;
  firstSessionDate: string;
  lastSessionDate?: string;
  isNewClient: boolean;
}

export interface ClientMetrics {
  newClients: number;
  returningClients: number;
  totalClients: number;
  retentionRate: number;
  topClients: TopClient[];
}

export interface BookingPatternDataPoint {
  dayOfWeek: number;
  hourOfDay: number;
  bookingCount: number;
  completedCount: number;
}

export interface DayOfWeekLabel {
  dayOfWeek: number;
  label: string;
  count: number;
}

export interface PeakHour {
  hour: number;
  count: number;
  timeRange: string;
}

export interface BookingPatterns {
  heatmapData: BookingPatternDataPoint[];
  byDayOfWeek: Record<string, number>;
  byHourOfDay: Record<string, number>;
  dayOfWeekLabels: DayOfWeekLabel[];
  peakHours: PeakHour[];
}

export interface DateRangeParams {
  startDate?: string;
  endDate?: string;
}

export interface RevenueParams extends DateRangeParams {
  period?: 'daily' | 'weekly' | 'monthly';
}

/**
 * Hook to fetch overview metrics with period-over-period comparison
 */
export function useOverviewMetrics(
  params?: DateRangeParams,
  options?: Omit<UseQueryOptions<OverviewMetrics>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['analytics', 'overview', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.startDate) searchParams.append('startDate', params.startDate);
      if (params?.endDate) searchParams.append('endDate', params.endDate);

      const response = await apiClient.get(
        `/therapist/analytics/overview?${searchParams.toString()}`
      );
      return response.data as OverviewMetrics;
    },
    ...options,
  });
}

/**
 * Hook to fetch revenue time series data
 */
export function useRevenueChart(
  params?: RevenueParams,
  options?: Omit<UseQueryOptions<RevenueChartData>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['analytics', 'revenue', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.period) searchParams.append('period', params.period);
      if (params?.startDate) searchParams.append('startDate', params.startDate);
      if (params?.endDate) searchParams.append('endDate', params.endDate);

      const response = await apiClient.get(
        `/therapist/analytics/revenue?${searchParams.toString()}`
      );
      return response.data as RevenueChartData;
    },
    ...options,
  });
}

/**
 * Hook to fetch session breakdown metrics
 */
export function useSessionsBreakdown(
  params?: DateRangeParams,
  options?: Omit<UseQueryOptions<SessionBreakdown>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['analytics', 'sessions', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.startDate) searchParams.append('startDate', params.startDate);
      if (params?.endDate) searchParams.append('endDate', params.endDate);

      const response = await apiClient.get(
        `/therapist/analytics/sessions?${searchParams.toString()}`
      );
      return response.data as SessionBreakdown;
    },
    ...options,
  });
}

/**
 * Hook to fetch client metrics and insights
 */
export function useClientMetrics(
  params?: DateRangeParams,
  options?: Omit<UseQueryOptions<ClientMetrics>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['analytics', 'clients', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.startDate) searchParams.append('startDate', params.startDate);
      if (params?.endDate) searchParams.append('endDate', params.endDate);

      const response = await apiClient.get(
        `/therapist/analytics/clients?${searchParams.toString()}`
      );
      return response.data as ClientMetrics;
    },
    ...options,
  });
}

/**
 * Hook to fetch booking patterns and trends
 */
export function useBookingPatterns(
  options?: Omit<UseQueryOptions<BookingPatterns>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['analytics', 'bookings'],
    queryFn: async () => {
      const response = await apiClient.get('/therapist/analytics/bookings');
      return response.data as BookingPatterns;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to refresh analytics cache
 */
export function useRefreshAnalytics() {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post('/therapist/analytics/refresh');
      return response.data;
    },
  });
}
